import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Box, Button, List, ListItem,
  ListItemIcon, ListItemText, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Chip, CircularProgress, Alert
} from '@mui/material';
import {
  Add, Schedule, Repeat, GetApp, Upload, Settings,
  CheckCircle, AttachMoney, Edit, Delete, AccessTime
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import paymentDraftService from '../../services/paymentDraftService';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand, text, bg, border } from '../../theme/colors';

/**
 * Smart Payment Actions
 * Quick actions and saved payment drafts
 */
const SmartPaymentActions = ({ onActionComplete, refreshTrigger }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);

  useEffect(() => {
    loadSavedDrafts();
  }, [user?.id, refreshTrigger]); // Reload when refreshTrigger changes

  const loadSavedDrafts = async () => {
    if (!user?.id) {
      setLoadingDrafts(false);
      return;
    }

    try {
      setLoadingDrafts(true);
      const drafts = await paymentDraftService.listDrafts(user.id, {
        includeExpired: false
      });
      setSavedDrafts(Array.isArray(drafts) ? drafts : []);
    } catch (error) {
      console.error('Failed to load saved payment drafts:', error);
      setSavedDrafts([]);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this saved payment?')) {
      return;
    }

    try {
      await paymentDraftService.deleteDraft(user.id, draftId);
      await loadSavedDrafts();
    } catch (error) {
      console.error('Failed to delete draft:', error);
      alert('Failed to delete saved payment. Please try again.');
    }
  };

  const handleResumeDraft = (draftId) => {
    // Trigger the wizard to open with specific draft ID
    if (onActionComplete) {
      onActionComplete('open_wizard', draftId);
    }
  };

  const quickActions = [
    {
      id: 'new_payment',
      icon: <Add sx={{ color: brand.primary }} />,
      title: 'New Payment',
      subtitle: 'Create a new payment',
      onClick: () => onActionComplete && onActionComplete('open_wizard')
    }
  ];

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType('');
  };

  const handleDialogSubmit = () => {
    console.log('Dialog submitted:', dialogType);
    handleCloseDialog();
    if (onActionComplete) onActionComplete();
  };

  return (
    <>
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Quick Actions
          </Typography>

          <List sx={{ mb: 2 }}>
            {quickActions.map((action, index) => (
              <React.Fragment key={action.id}>
                <ListItem
                  button
                  onClick={action.onClick}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': { bgcolor: bg.subtle }
                  }}
                >
                  <ListItemIcon>{action.icon}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {action.title}
                      </Typography>
                    }
                    secondary={action.subtitle}
                  />
                </ListItem>
                {index < quickActions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Saved Payments
          </Typography>

          {loadingDrafts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : savedDrafts.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {savedDrafts.map((draft) => {
                const totalAmount = Array.isArray(draft.lineItems) 
                  ? draft.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
                  : 0;
                const updatedDate = new Date(draft.updatedAt || draft.createdAt);
                const formattedDate = updatedDate.toLocaleDateString();

                return (
                  <Box
                    key={draft.id}
                    sx={{
                      p: 2,
                      border: `1px solid ${border.light}`,
                      borderRadius: 1,
                      bgcolor: bg.subtle,
                      transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: brand.primary,
                        bgcolor: `${brand.primary}05`
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ${totalAmount.toFixed(2)} {draft.paymentData?.currency || 'USD'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: text.secondary, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <AccessTime sx={{ fontSize: 14 }} />
                          Saved {formattedDate}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleResumeDraft(draft.id)}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          <Edit sx={{ fontSize: 18 }} />
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => handleDeleteDraft(draft.id)}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          <Delete sx={{ fontSize: 18 }} />
                        </Button>
                      </Box>
                    </Box>
                    {draft.lineItems && draft.lineItems.length > 0 && (
                      <Typography variant="caption" sx={{ color: text.secondary }}>
                        {draft.lineItems.length} line item{draft.lineItems.length !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              No saved payments. Click "Save for Later" in the payment wizard to save your progress.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Bulk Operations
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Upload />}
              fullWidth
              sx={{ textTransform: 'none', borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
              onClick={() => navigate('/payments/import')}
            >
              Import
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<GetApp />}
              fullWidth
              sx={{ textTransform: 'none', borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
              onClick={() => navigate('/payments/export')}
            >
              Export
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog for Schedule/Recurring/Bulk actions */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={handleCloseDialog} />
        <DialogTitle>
          {dialogType === 'schedule' && 'Schedule Payment'}
          {dialogType === 'recurring' && 'Set Up Recurring Payment'}
          {dialogType === 'bulk' && 'Bulk Release Payments'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'schedule' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Payment Amount"
                type="number"
                fullWidth
                InputProps={{
                  startAdornment: <AttachMoney />
                }}
              />
              <TextField
                label="Schedule Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Contractor"
                select
                fullWidth
              >
                <MenuItem value="contractor1">John Smith</MenuItem>
                <MenuItem value="contractor2">Jane Doe</MenuItem>
              </TextField>
              <TextField
                label="Description"
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          )}

          {dialogType === 'recurring' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Payment Amount"
                type="number"
                fullWidth
                InputProps={{
                  startAdornment: <AttachMoney />
                }}
              />
              <TextField
                label="Frequency"
                select
                fullWidth
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Bi-weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </TextField>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date (Optional)"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}

          {dialogType === 'bulk' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Select payments to release in bulk:
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Milestone 1 - John Smith"
                    secondary="$5,000"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Milestone 2 - Jane Doe"
                    secondary="$7,500"
                  />
                </ListItem>
              </List>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>
                Total: $12,500
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDialogSubmit}
            sx={{
              bgcolor: brand.primary,
              '&:hover': { bgcolor: brand.primaryDark },
              textTransform: 'none'
            }}
          >
            {dialogType === 'bulk' ? 'Release Payments' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SmartPaymentActions;
